import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PhoneList.css';
import phoneImage from '../../assets/images/phone-11.png';
import iconClose from '../../assets/images/icon-back.svg';
import PhoneDetail from './PhoneDetail';
import skLogo from '../../assets/images/sk-logo.svg';
import ktLogo from '../../assets/images/logo_kt.svg';
import uplusLogo from '../../assets/images/logo_uplus.svg';
import iconMemory from '../../assets/images/icon_memory.svg';
import iconToDo from '../../assets/images/icon_to-do.svg';
import iconNotice from '../../assets/images/icon_notice.svg';
import iconSupport from '../../assets/images/icon_support.svg';
import iconCall from '../../assets/images/icon_call.svg';
import iconBox from '../../assets/images/icon_box.svg';
import iconCheck from '../../assets/images/icon_check.svg';
import iconCaution from '../../assets/images/icon_caution.svg';
import { supabase } from '../../lib/supabase';
import { PhoneModel, PhoneColor, PhoneAddon, WatchModel, WatchColor, WatchPricing } from '../../types/products';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../contexts/AlertContext';

interface PhoneListProps {
  onBack: () => void;
  onSwitchToInternet: () => void;
}

const PhoneList: React.FC<PhoneListProps> = ({ onBack, onSwitchToInternet }) => {
  const navigate = useNavigate();
  const { customer } = useAuth();
  const { showAlert } = useAlert();
  const [selectedCarrier, setSelectedCarrier] = useState('KT');
  const [selectedType, setSelectedType] = useState('갤럭시');
  const [selectedPhone, setSelectedPhone] = useState<PhoneModel | null>(null);
  const [selectedCapacity, setSelectedCapacity] = useState('128GB');
  const [selectedColor, setSelectedColor] = useState<PhoneColor | null>(null);
  const [selectedDiscountMethod, setSelectedDiscountMethod] = useState('공시지원');
  const [selectedInstallment, setSelectedInstallment] = useState('24개월');
  const [showDetail, setShowDetail] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false);
  const [isConsultExpanded, setIsConsultExpanded] = useState(false);
  const [isOptionsExpanded, setIsOptionsExpanded] = useState(true);
  const [selectedInfoTab, setSelectedInfoTab] = useState('요금제');
  const [selectedAddons, setSelectedAddons] = useState<{ [key: string]: boolean }>({});
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0); // 선택된 요금제 인덱스

  // Supabase에서 불러온 상품 데이터
  const [phoneModels, setPhoneModels] = useState<PhoneModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableCarriers, setAvailableCarriers] = useState<string[]>(['KT', 'SKT', 'LG', 'MVNO']);
  const [availableStorages, setAvailableStorages] = useState<string[]>(['64GB', '128GB', '256GB', '512GB', '1TB']);

  // 워치 관련 상태
  const [watchModels, setWatchModels] = useState<WatchModel[]>([]);
  const [selectedWatch, setSelectedWatch] = useState<WatchModel | null>(null);
  const [selectedWatchSize, setSelectedWatchSize] = useState<string>('');
  const [selectedWatchColor, setSelectedWatchColor] = useState<WatchColor | null>(null);
  const [selectedWatchStorage, setSelectedWatchStorage] = useState<string>('');
  const [watchPricing, setWatchPricing] = useState<WatchPricing | null>(null);

  // 선택된 통신사의 요금제 목록 가져오기
  const getCurrentPlans = (): { name: string; price: number }[] => {
    if (!selectedPhone || !selectedPhone.plans) {
      return [{ name: '프리미엄 10.9', price: 109000 }]; // 기본값
    }

    // 통신사 매핑 (버튼 값 -> DB 값)
    const carrierMap: { [key: string]: string } = {
      'KT': 'KT',
      'SK': 'SKT',
      'LG U+': 'LG',
      '알뜰폰': 'MVNO'
    };
    const dbCarrier = carrierMap[selectedCarrier] || selectedCarrier;

    const carrierPlans = selectedPhone.plans[dbCarrier];
    if (carrierPlans && carrierPlans.length > 0) {
      return carrierPlans;
    }

    return [{ name: '프리미엄 10.9', price: 109000 }]; // 기본값
  };

  // 선택된 통신사의 요금제 정보 가져오기
  const getCurrentPlanInfo = () => {
    const plans = getCurrentPlans();
    // 선택된 인덱스가 유효한지 확인
    if (selectedPlanIndex >= 0 && selectedPlanIndex < plans.length) {
      return plans[selectedPlanIndex];
    }
    return plans[0]; // 기본값은 첫 번째 요금제
  };

  // 선택된 통신사의 부가서비스 정보 가져오기
  const getCurrentAddons = () => {
    if (!selectedPhone || !selectedPhone.addons) {
      return [];
    }

    // 통신사 매핑 (버튼 값 -> DB 값)
    const carrierMap: { [key: string]: string } = {
      'KT': 'KT',
      'SK': 'SKT',
      'LG U+': 'LG',
      '알뜰폰': 'MVNO'
    };
    const dbCarrier = carrierMap[selectedCarrier] || selectedCarrier;

    const carrierAddons = selectedPhone.addons[dbCarrier];
    return carrierAddons || [];
  };

  const currentPlan = getCurrentPlanInfo();
  const basePlanPrice = currentPlan.price; // 요금제 기본 가격
  const currentAddons = getCurrentAddons(); // 현재 통신사의 부가서비스

  // 요금제 가격 (선택약정 시 25% 할인 적용)
  const getPlanPrice = () => {
    if (selectedDiscountMethod === '선택약정') {
      return Math.floor(basePlanPrice * 0.75); // 25% 할인
    }
    return basePlanPrice;
  };

  // 부가서비스 총 금액 계산 (선택된 것만)
  const getTotalAddonsPrice = () => {
    return currentAddons.reduce((total: number, addon: PhoneAddon) => {
      if (selectedAddons[addon.name]) {
        return total + (addon.price || 0);
      }
      return total;
    }, 0);
  };

  // 선택된 부가서비스의 추가 지원금 총액 계산
  const getTotalAddonsSupport = () => {
    return currentAddons.reduce((total: number, addon: PhoneAddon) => {
      if (selectedAddons[addon.name] && addon.additionalSupport) {
        return total + addon.additionalSupport;
      }
      return total;
    }, 0);
  };

  const planPrice = getPlanPrice();
  const planDiscount = selectedDiscountMethod === '선택약정' ? Math.floor(basePlanPrice * 0.25) : 0;
  const addonsPrice = getTotalAddonsPrice();

  const fetchPhoneModels = async () => {
    setLoading(true);
    try {
      // 휴대폰 목록은 기기 종류만으로 필터링 (통신사는 나중에 선택)
      // 모델명 기준으로 그룹화하여 중복 제거
      const { data } = await supabase
        .from('phone_models')
        .select('*')
        .eq('brand', selectedType)
        .eq('is_active', true)
        .order('original_price', { ascending: true });

      // 모델명 기준으로 중복 제거 (같은 모델이 여러 통신사에 있을 수 있음)
      const uniqueModels = data?.reduce((acc: PhoneModel[], current) => {
        const exists = acc.find(item => item.model_name === current.model_name);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);

      setPhoneModels(uniqueModels || []);
      setSelectedPhone(null); // 기기종류 변경 시 선택 초기화
      setSelectedColor(null); // 색상도 초기화
    } catch (error) {
      console.error('Error fetching phone models:', error);
    } finally {
      setLoading(false);
    }
  };

  // 워치 모델 불러오기
  const fetchWatchModels = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('watch_models')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (data) {
        // available_colors를 JSON 파싱
        const parsedData = data.map((watch: any) => ({
          ...watch,
          available_colors: typeof watch.available_colors === 'string'
            ? JSON.parse(watch.available_colors)
            : watch.available_colors,
          variant_images: typeof watch.variant_images === 'string'
            ? JSON.parse(watch.variant_images)
            : watch.variant_images
        }));
        setWatchModels(parsedData);
      }
      setSelectedWatch(null);
      setSelectedWatchSize('');
      setSelectedWatchColor(null);
      setSelectedWatchStorage('');
      setWatchPricing(null);
    } catch (error) {
      console.error('Error fetching watch models:', error);
    } finally {
      setLoading(false);
    }
  };

  // 워치 가격 정보 불러오기
  const fetchWatchPricing = async (watchId: string, storage: string, size: string, carrier: string) => {
    try {
      const { data, error } = await supabase
        .from('watch_pricing')
        .select('*')
        .eq('watch_model_id', watchId)
        .eq('storage', storage)
        .eq('size', size)
        .eq('carrier', carrier)
        .maybeSingle();

      if (error) {
        console.log('워치 가격 정보 없음:', { watchId, storage, size, carrier });
        setWatchPricing(null);
        return;
      }

      setWatchPricing(data);
    } catch (error) {
      console.log('워치 가격 조회 오류:', error);
      setWatchPricing(null);
    }
  };

  // 선택한 휴대폰의 사용 가능한 통신사 및 용량 확인
  useEffect(() => {
    const checkAvailableOptions = async () => {
      if (!selectedPhone) {
        setAvailableCarriers(['KT', 'SKT', 'LG', 'MVNO']);
        return;
      }

      try {
        const { data } = await supabase
          .from('phone_models')
          .select('carrier, available_storages')
          .eq('model_name', selectedPhone.model_name)
          .eq('brand', selectedType)
          .eq('is_active', true);

        if (data) {
          // DB에서 조회한 통신사 목록 (쉼표로 구분된 문자열을 배열로 변환)
          const allCarriers = data.flatMap(item =>
            item.carrier.split(',').map((c: string) => c.trim())
          );
          // 중복 제거
          const uniqueCarriers = Array.from(new Set(allCarriers));
          setAvailableCarriers(uniqueCarriers);

          // DB 값을 버튼 값으로 변환 (역매핑)
          const reverseCarrierMap: { [key: string]: string } = {
            'KT': 'KT',
            'SKT': 'SK',
            'LG': 'LG U+',
            'MVNO': '알뜰폰'
          };

          // 사용 가능한 버튼 값들
          const availableButtonCarriers = uniqueCarriers
            .map(dbCarrier => reverseCarrierMap[dbCarrier])
            .filter(Boolean);

          // 첫 번째 사용 가능한 통신사로 자동 선택
          if (availableButtonCarriers.length > 0) {
            setSelectedCarrier(availableButtonCarriers[0]);
          }

          // 용량 목록 (선택된 휴대폰의 available_storages 사용)
          if (selectedPhone.available_storages && selectedPhone.available_storages.length > 0) {
            setAvailableStorages(selectedPhone.available_storages);

            // 현재 선택된 용량이 사용 불가능하면 사용 가능한 첫 번째로 변경
            if (!selectedPhone.available_storages.includes(selectedCapacity)) {
              setSelectedCapacity(selectedPhone.available_storages[0] || '128GB');
            }
          } else {
            // available_storages가 null이거나 비어있으면 모든 용량 활성화
            setAvailableStorages(['64GB', '128GB', '256GB', '512GB', '1TB']);
          }
        }
      } catch (error) {
        console.error('Error checking available options:', error);
      }
    };

    checkAvailableOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhone]);

  // Supabase에서 핸드폰/워치 모델 불러오기
  useEffect(() => {
    if (selectedType === '워치') {
      fetchWatchModels();
    } else {
      fetchPhoneModels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  // 워치 옵션 변경 시 가격 정보 조회
  useEffect(() => {
    if (selectedWatch && selectedWatchStorage && selectedWatchSize && selectedCarrier) {
      const carrierMap: { [key: string]: string } = {
        'KT': 'KT',
        'SK': 'SKT',
        'LG U+': 'LG',
        '알뜰폰': 'MVNO'
      };
      const dbCarrier = carrierMap[selectedCarrier] || selectedCarrier;
      fetchWatchPricing(selectedWatch.id, selectedWatchStorage, selectedWatchSize, dbCarrier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWatch, selectedWatchStorage, selectedWatchSize, selectedCarrier]);

  // 부가서비스가 변경될 때마다 선택 상태 초기화 (필수 서비스는 자동 선택)
  useEffect(() => {
    const addons = getCurrentAddons();
    const newSelectedAddons: { [key: string]: boolean } = {};

    addons.forEach((addon: PhoneAddon) => {
      // 필수 서비스는 자동으로 선택
      newSelectedAddons[addon.name] = addon.required || false;
    });

    setSelectedAddons(newSelectedAddons);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhone, selectedCarrier]);

  // 통신사 변경 시 요금제 인덱스 초기화
  useEffect(() => {
    setSelectedPlanIndex(0);
  }, [selectedCarrier]);

  // 할인 정보 가져오기 (통신사, 요금제명, 용량, 기기변경/번호이동, 할인방법에 따라)
  const getDiscountInfo = () => {
    if (!selectedPhone) return { commonSupport: 0, bbibbiDiscount: 0 };

    // bbibbi_discount가 숫자인 경우 (레거시)
    if (typeof selectedPhone.bbibbi_discount === 'number') {
      return {
        commonSupport: selectedPhone.common_support || 0,
        bbibbiDiscount: selectedPhone.bbibbi_discount
      };
    }

    // 객체 구조인 경우
    // 통신사 매핑 (버튼 값 -> DB 값)
    const carrierMap: { [key: string]: string } = {
      'KT': 'KT',
      'SK': 'SKT',
      'LG U+': 'LG',
      '알뜰폰': 'MVNO'
    };
    const dbCarrier = carrierMap[selectedCarrier] || selectedCarrier;

    // 변경유형 매핑 (dbCarrier와 customer carrier 비교)
    const changeType = customer?.carrier === dbCarrier ? 'device_change' : 'number_transfer';

    // 할인방법 매핑
    const discountMethodMap: { [key: string]: string } = {
      '공시지원': 'public',
      '선택약정': 'optional'
    };
    const dbDiscountMethod = discountMethodMap[selectedDiscountMethod] || 'public';

    // 현재 선택된 요금제명 가져오기
    const planName = currentPlan.name;

    try {
      // 통신사 > 요금제명 > 용량 > 변경유형 > 할인방법 순서로 접근
      const discountData = selectedPhone.bbibbi_discount as unknown as {
        [carrier: string]: {
          [planName: string]: {
            [storage: string]: {
              [changeType: string]: {
                [discountMethod: string]: {
                  common_support: number;
                  bbibbi_discount: number;
                };
              };
            };
          };
        };
      };

      const info = discountData?.[dbCarrier]?.[planName]?.[selectedCapacity]?.[changeType]?.[dbDiscountMethod];

      return {
        commonSupport: info?.common_support || 0,
        bbibbiDiscount: info?.bbibbi_discount || 0
      };
    } catch (error) {
      console.error('Error getting discount info:', error);
      return { commonSupport: 0, bbibbiDiscount: 0 };
    }
  };

  // 삐삐 할인 값 가져오기 (부가서비스 추가지원금 포함)
  const getBbibbiDiscount = () => {
    const baseDiscount = getDiscountInfo().bbibbiDiscount;
    const addonsSupport = getTotalAddonsSupport();
    return baseDiscount + addonsSupport;
  };

  // 출고가 가져오기 (선택한 용량에 맞는 가격)
  const getStoragePrice = () => {
    if (!selectedPhone) return 0;
    if (selectedPhone.storage_prices && selectedPhone.storage_prices[selectedCapacity]) {
      return selectedPhone.storage_prices[selectedCapacity];
    }
    return selectedPhone.original_price || 0;
  };

  // 총 가격 계산 (구매 원금)
  const calculatePhonePrice = () => {
    if (!selectedPhone) return 0;
    const storagePrice = getStoragePrice(); // 출고가
    const { commonSupport } = getDiscountInfo();
    const totalBbibbiDiscount = getBbibbiDiscount(); // 부가서비스 추가지원금 포함된 삐삐 할인

    // 공시지원은 '공시지원' 선택 시만 적용
    if (selectedDiscountMethod === '공시지원') {
      return storagePrice - commonSupport - totalBbibbiDiscount;
    } else {
      // 선택약정 선택 시에는 삐삐할인만 적용
      return storagePrice - totalBbibbiDiscount;
    }
  };

  // 할부 이자율 가져오기
  const getInstallmentInterestRate = () => {
    switch (selectedInstallment) {
      case '24개월': return 0.059; // 5.9%
      case '30개월': return 0.064; // 6.4%
      case '36개월': return 0.069; // 6.9%
      default: return 0;
    }
  };

  // 월 할부금 계산 (원리금 균등분할상환)
  const getMonthlyDevicePayment = () => {
    if (selectedInstallment === '일시불') return 0;

    const principal = calculatePhonePrice(); // 원금
    const months = parseInt(selectedInstallment);
    const annualRate = getInstallmentInterestRate();

    if (annualRate === 0) {
      // 무이자인 경우
      return Math.floor(principal / months);
    }

    const monthlyRate = annualRate / 12; // 월 이자율

    // 원리금 균등분할상환 공식
    // 월 납부금 = 원금 × (월 이자율 × (1 + 월 이자율)^개월수) / ((1 + 월 이자율)^개월수 - 1)
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);

    return Math.floor(monthlyPayment);
  };

  // 월 할부 원금
  const getMonthlyPrincipal = () => {
    if (selectedInstallment === '일시불') return 0;
    const principal = calculatePhonePrice();
    const months = parseInt(selectedInstallment);
    return Math.floor(principal / months);
  };

  // 월 할부 이자
  const getMonthlyInterest = () => {
    return getMonthlyDevicePayment() - getMonthlyPrincipal();
  };

  const phoneDevicePrice = calculatePhonePrice();
  const monthlyDevicePayment = getMonthlyDevicePayment();
  const totalMonthlyPrice = phoneDevicePrice > 0 ? monthlyDevicePayment + planPrice + addonsPrice : 0;

  // 워치 가격 계산
  const calculateWatchPrice = () => {
    if (!watchPricing) return 0;
    return watchPricing.original_price - watchPricing.common_support - watchPricing.store_support;
  };

  // 워치 이미지 URL 가져오기
  const getWatchImageUrl = () => {
    if (!selectedWatch) return phoneImage;
    if (selectedWatchSize && selectedWatchColor && selectedWatch.variant_images) {
      const key = `${selectedWatchSize}_${selectedWatchColor.name}`;
      if (selectedWatch.variant_images[key]) {
        return selectedWatch.variant_images[key];
      }
    }
    return selectedWatch.image_url || phoneImage;
  };

  const watchDevicePrice = calculateWatchPrice();

  // 채팅 상담 요청
  const handleChatConsult = async () => {
    // 워치일 때와 핸드폰일 때 다르게 체크
    if (selectedType === '워치') {
      if (!selectedWatch) {
        showAlert('상품을 선택해주세요.');
        return;
      }
    } else {
      if (phoneDevicePrice === 0) {
        showAlert('상품을 선택해주세요.');
        return;
      }
    }

    if (!customer) {
      showAlert('로그인이 필요합니다.');
      return;
    }

    try {
      // 통신사 매핑 (버튼 값 -> DB 값)
      const carrierMap: { [key: string]: string } = {
        'KT': 'KT',
        'SK': 'SKT',
        'LG U+': 'LG',
        '알뜰폰': 'MVNO'
      };
      const dbCarrier = carrierMap[selectedCarrier] || selectedCarrier;

      // 채팅방 생성
      const { data: chatRoom, error } = await supabase
        .from('chat_rooms')
        .insert([{
          customer_id: customer.id,
          status: 'waiting',
          carrier: dbCarrier,
          product_type: selectedType === '워치' ? 'watch' : 'phone',
          phone_model_id: selectedType === '워치' ? null : selectedPhone?.id
        }])
        .select()
        .single();

      if (error) throw error;

      // 초기 상담 요청 메시지 전송
      let productInfo = '안녕하세요! 다음 상품에 대해 상담 요청드립니다:\n\n';

      if (selectedType === '워치' && selectedWatch) {
        // 워치 상담 메시지
        productInfo += '=== 워치 상품 ===\n';
        productInfo += `▪ 기기: ${selectedWatch.model_name}\n`;
        productInfo += `▪ 사이즈: ${selectedWatchSize}\n`;
        productInfo += `▪ 용량: ${selectedWatchStorage}\n`;
        if (selectedWatchColor) {
          productInfo += `▪ 색상: ${selectedWatchColor.name}\n`;
        }
        productInfo += `▪ 통신사: ${selectedCarrier}\n`;
        if (watchPricing) {
          productInfo += `▪ 출고가: ${watchPricing.original_price.toLocaleString()}원\n`;
          productInfo += `▪ 공시지원: -${watchPricing.common_support.toLocaleString()}원\n`;
          productInfo += `▪ 삐삐 할인: -${watchPricing.store_support.toLocaleString()}원\n`;
        }
        productInfo += `\n💰 구매 원금: ${watchDevicePrice.toLocaleString()}원`;
      } else if (selectedPhone) {
        // 핸드폰 상담 메시지
        const { commonSupport, bbibbiDiscount } = getDiscountInfo();
        productInfo += '=== 핸드폰 상품 ===\n';
        productInfo += `▪ 기기: ${selectedPhone.model_name}\n`;
        productInfo += `▪ 용량: ${selectedCapacity}\n`;
        if (selectedColor) {
          productInfo += `▪ 색상: ${selectedColor.name}\n`;
        }
        productInfo += `▪ 출고가: ${getStoragePrice().toLocaleString()}원\n`;
        if (selectedDiscountMethod === '공시지원') {
          productInfo += `▪ 공시지원: -${commonSupport.toLocaleString()}원\n`;
        }
        productInfo += `▪ 삐삐 할인: -${bbibbiDiscount.toLocaleString()}원\n`;
        productInfo += `▪ 기기 금액: ${phoneDevicePrice.toLocaleString()}원\n`;
        productInfo += `▪ 할부: ${selectedInstallment}\n`;
        if (selectedInstallment !== '일시불') {
          productInfo += `▪ 월 기기 할부금: ${getMonthlyPrincipal().toLocaleString()}원\n`;
          productInfo += `▪ 월 기기 할부이자: ${getMonthlyInterest().toLocaleString()}원\n`;
        }
        productInfo += `\n▪ 요금제: ${currentPlan.name} (${planPrice.toLocaleString()}원/월)\n`;
        if (selectedDiscountMethod === '선택약정') {
          productInfo += `  - 선택약정 25% 할인 적용\n`;
        }

        // 부가서비스 정보 추가 (선택된 것만)
        const selectedAddonsList = currentAddons.filter((addon: PhoneAddon) =>
          selectedAddons[addon.name] === true
        );

        if (selectedAddonsList.length > 0) {
          productInfo += `\n▪ 부가서비스:\n`;
          selectedAddonsList.forEach((addon: PhoneAddon) => {
            productInfo += `  - ${addon.name}: ${addon.price.toLocaleString()}원/월`;
            if (addon.required) {
              productInfo += ` (필수)`;
            }
            productInfo += `\n`;
          });
        }
        productInfo += `\n💰 월 납부 예상 금액: ${totalMonthlyPrice.toLocaleString()}원`;
      }

      const { error: messageError } = await supabase.from('messages').insert([{
        room_id: chatRoom.id,
        sender_id: customer.id,
        message_type: 'text',
        content: productInfo,
        is_read: false
      }]);

      if (messageError) {
        console.error('Error sending initial message:', messageError);
        // 메시지 전송 실패해도 채팅방으로 이동
      }

      // 상품 화면 닫기
      onBack();

      // 채팅 탭으로 전환하고 채팅방 열기
      navigate('/main', { state: { tab: 'chat', chatRoomId: chatRoom.id } });
    } catch (error: any) {
      console.error('Error creating chat room:', error);
      console.error('Error details:', error?.message, error?.details);
      showAlert('상담 요청 중 오류가 발생했습니다: ' + (error?.message || '알 수 없는 오류'));
    }
  };

  if (showDetail) {
    return <PhoneDetail onBack={() => setShowDetail(false)} />;
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setDragStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const diff = dragStartY - currentY;
    if (diff > 0 && diff < 400) {
      setDragOffset(diff);
    } else if (diff < 0) {
      setDragOffset(0);
    }
  };

  const handleTouchEnd = () => {
    if (dragOffset > 100) {
      setIsBottomSheetExpanded(true);
      setDragOffset(0);
    } else {
      setIsBottomSheetExpanded(false);
      setDragOffset(0);
    }
  };

  return (
    <div className="phone-list-container">
      <div className="phone-list-header">
        <div className="phone-list-header-wrapper">
          <img src={iconClose} alt="뒤로가기" className="header-icon" onClick={onBack} />
          <h1 className="phone-list-title">핸드폰/워치</h1>
          <div className="header-icon" style={{ width: '24px', height: '24px' }}></div>
        </div>
      </div>

      <div className="phone-list-content">
        <h2 className="phone-list-page-title">핸드폰/워치</h2>
        <div className="tab-selector">
          <button className="tab-btn" onClick={onSwitchToInternet}>인터넷 / TV</button>
          <button className="tab-btn active">핸드폰/워치</button>
        </div>

        <div className="section">
          <div className="section-header">
            <h3 className="section-title">기기 종류</h3>
            <p className="section-desc">기기 종류를 선택해 주세요.</p>
          </div>
          <div className="chip-group">
            {['갤럭시', '아이폰', '기타', '워치'].map((type) => (
              <button
                key={type}
                className={`chip ${selectedType === type ? 'active' : ''}`}
                onClick={() => setSelectedType(type)}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        <div className="divider" />

        {/* 휴대폰 목록 (워치가 아닐 때) */}
        {selectedType !== '워치' && (
          <div className="section">
            <div className="section-header">
              <h3 className="section-title">휴대폰</h3>
            </div>
            {loading ? (
              <div className="loading">상품을 불러오는 중...</div>
            ) : phoneModels.length === 0 ? (
              <div className="empty">해당 조건의 상품이 없습니다.</div>
            ) : (
              <div className="phone-list">
                {phoneModels.map((phone) => (
                  <button
                    key={phone.id}
                    className={'phone-item ' + (selectedPhone?.id === phone.id ? 'active' : '')}
                    onClick={() => {
                      setSelectedPhone(phone);
                      // 휴대폰 선택 시 첫 번째 색상 자동 선택
                      if (phone.available_colors && phone.available_colors.length > 0) {
                        setSelectedColor(phone.available_colors[0]);
                      } else {
                        setSelectedColor(null);
                      }
                    }}
                  >
                    {phone.model_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 워치 목록 */}
        {selectedType === '워치' && (
          <div className="section">
            <div className="section-header">
              <h3 className="section-title">워치</h3>
            </div>
            {loading ? (
              <div className="loading">상품을 불러오는 중...</div>
            ) : watchModels.length === 0 ? (
              <div className="empty">해당 조건의 상품이 없습니다.</div>
            ) : (
              <div className="phone-list">
                {watchModels.map((watch) => (
                  <button
                    key={watch.id}
                    className={'phone-item ' + (selectedWatch?.id === watch.id ? 'active' : '')}
                    onClick={() => {
                      setSelectedWatch(watch);
                      // 워치 선택 시 첫 번째 옵션들 자동 선택
                      if (watch.available_sizes && watch.available_sizes.length > 0) {
                        setSelectedWatchSize(watch.available_sizes[0]);
                      }
                      if (watch.available_colors && watch.available_colors.length > 0) {
                        setSelectedWatchColor(watch.available_colors[0]);
                      }
                      if (watch.available_storages && watch.available_storages.length > 0) {
                        setSelectedWatchStorage(watch.available_storages[0]);
                      }
                    }}
                  >
                    {watch.model_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 휴대폰 선택 옵션 */}
        {selectedType !== '워치' && selectedPhone && (
          <>
            <div className="divider" />

            <div className="section">
              <div className="section-header-with-toggle">
                <h3 className="section-title">선택 옵션</h3>
                <button
                  className="toggle-btn"
                  onClick={() => setIsOptionsExpanded(!isOptionsExpanded)}
                >
                  {isOptionsExpanded ? '▲' : '▼'}
                </button>
              </div>
            </div>

            {isOptionsExpanded && (
              <>
                <div className="info-card">
                  <img
                    src={selectedColor?.image_url || selectedPhone?.image_url || phoneImage}
                    alt="핸드폰"
                    className="info-image"
                  />
                  <div className="info-text">
                    <p className="info-title">핸드폰 / 알뜰폰</p>
                    <p className="info-desc">
                      {selectedPhone ? `${selectedPhone.model_name}${selectedColor ? ` · ${selectedColor.name}` : ''}` : '휴대폰 예시 가격안내 · 통신사/단말/조건/요금제 연동'}
                    </p>
                  </div>
                </div>

                <div className="divider" />

                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">색상</h3>
                    <p className="section-desc">원하시는 색상을 선택해 주세요.</p>
                  </div>
                  <div className="color-group">
                    {selectedPhone?.available_colors && selectedPhone.available_colors.length > 0 ? (
                      selectedPhone.available_colors.map((color) => (
                        <button
                          key={color.name}
                          className={`color-chip ${selectedColor?.name === color.name ? 'active' : ''}`}
                          onClick={() => setSelectedColor(color)}
                          style={{ backgroundColor: color.hex }}
                          title={color.name}
                        >
                          {selectedColor?.name === color.name && (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M13.3333 4L6 11.3333L2.66667 8"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </button>
                      ))
                    ) : (
                      <p className="section-desc">사용 가능한 색상이 없습니다.</p>
                    )}
                  </div>
                </div>

                <div className="divider" />

                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">
                      통신사
                      {selectedCarrier && (
                        <span className="carrier-type-badge">
                          {selectedCarrier === 'LG U+' ? 'LG' : selectedCarrier} {customer?.carrier === (selectedCarrier === 'SK' ? 'SKT' : selectedCarrier === 'LG U+' ? 'LG' : selectedCarrier === '알뜰폰' ? 'MVNO' : selectedCarrier) ? '기기변경' : '번호이동'}
                        </span>
                      )}
                    </h3>
                    <p className="section-desc">이용할 통신사를 선택해 주세요.</p>
                  </div>
                  <div className="chip-group">
                    {['KT', 'SK', 'LG U+', '알뜰폰'].map((carrier) => {
                      // DB 값과 버튼 값 매핑
                      const carrierMap: { [key: string]: string } = {
                        'KT': 'KT',
                        'SK': 'SKT',
                        'LG U+': 'LG',
                        '알뜰폰': 'MVNO'
                      };
                      const dbCarrier = carrierMap[carrier];
                      const isAvailable = selectedPhone ? availableCarriers.includes(dbCarrier) : true;

                      return (
                        <button
                          key={carrier}
                          className={`chip ${selectedCarrier === carrier ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
                          onClick={() => {
                            if (isAvailable) {
                              setSelectedCarrier(carrier);
                            }
                          }}
                        >
                          {carrier}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="divider" />

                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">용량</h3>
                    <p className="section-desc">원하시는 저장 용량을 선택해 주세요.</p>
                  </div>
                  <div className="chip-group">
                    {['64GB', '128GB', '256GB', '512GB', '1TB'].map((capacity) => {
                      const isAvailable = availableStorages.includes(capacity);
                      return (
                        <button
                          key={capacity}
                          className={`chip ${selectedCapacity === capacity ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
                          onClick={() => isAvailable && setSelectedCapacity(capacity)}
                          disabled={!isAvailable}
                        >
                          {capacity}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="divider" />

                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">할인 방법</h3>
                    <p className="section-desc">원하시는 할인 방법을 선택해 주세요.</p>
                  </div>
                  <div className="chip-group">
                    {['공시지원', '선택약정'].map((method) => (
                      <button
                        key={method}
                        className={`chip ${selectedDiscountMethod === method ? 'active' : ''}`}
                        onClick={() => setSelectedDiscountMethod(method)}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="divider" />

                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">할부 개월수</h3>
                    <p className="section-desc">할부 개월수를 선택해 주세요.</p>
                  </div>
                  <div className="chip-group">
                    {['일시불', '24개월', '30개월', '36개월'].map((installment) => (
                      <button
                        key={installment}
                        className={`chip ${selectedInstallment === installment ? 'active' : ''}`}
                        onClick={() => setSelectedInstallment(installment)}
                      >
                        {installment}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="divider" />

                {/* 요금제 선택 */}
                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">요금제</h3>
                    <p className="section-desc">요금제를 선택해 주세요.</p>
                  </div>
                  <div className="chip-group">
                    {getCurrentPlans().map((plan, index) => (
                      <button
                        key={plan.name}
                        className={`chip ${selectedPlanIndex === index ? 'active' : ''}`}
                        onClick={() => setSelectedPlanIndex(index)}
                      >
                        {plan.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="divider" />

                {/* 정보 탭 */}
                <div className="info-tabs">
                  {['요금제', '필독사항', '개통방법'].map((tab) => (
                    <button
                      key={tab}
                      className={`info-tab ${selectedInfoTab === tab ? 'active' : ''}`}
                      onClick={() => setSelectedInfoTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* 탭 컨텐츠 */}
                <div className="info-tab-content">
                  {selectedInfoTab === '요금제' && (
                    <div className="tab-content-section">
                      <h3 className="tab-content-title">요금제</h3>
                      <div className="plan-info-box">
                        <p className="plan-highlight">{currentPlan.name} 요금제를 <span className="highlight-badge">189일</span> 사용해요</p>
                      </div>
                      <div className="plan-details-box">
                        <div className="plan-detail-item">
                          <span className="plan-detail-label">데이터</span>
                          <span className="plan-detail-value">완전무제한</span>
                        </div>
                        <div className="plan-detail-item">
                          <span className="plan-detail-label">통화, 문자</span>
                          <span className="plan-detail-value">통화 무제한, 문자 무제한</span>
                        </div>
                        <div className="plan-detail-item">
                          <span className="plan-detail-label">부가통화</span>
                          <span className="plan-detail-value">300분</span>
                        </div>
                        <div className="plan-detail-item">
                          <span className="plan-detail-label">기본 월 요금</span>
                          <span className="plan-detail-value">{basePlanPrice.toLocaleString()}원</span>
                        </div>
                        <div className="plan-detail-item">
                          <span className="plan-detail-label">유지기간</span>
                          <span className="plan-detail-value">189일</span>
                        </div>
                      </div>

                      <div className="plan-penalty-section">
                        <h4 className="plan-penalty-title">위약금 및 요금제 변경 안내</h4>
                        <table className="plan-penalty-table">
                          <thead>
                            <tr>
                              <th>변경 시점</th>
                              <th>안내사항</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>개통 다음날부터 만 189일 이내</td>
                              <td>공동지원금 및 삐삐 할인금 일부가 위약금으로 부과</td>
                            </tr>
                            <tr>
                              <td>개통 다음날부터 만 189일 이후</td>
                              <td>42,000원 이상 요금제에서 자유롭게 변경 가능</td>
                            </tr>
                          </tbody>
                        </table>
                        <p className="plan-penalty-note">* 선택약정에 추가지원금을 받지 않았다면, 약정일 이후 금액 제한 없이 모든 요금제로 변경할 수 있어요.</p>
                      </div>
                    </div>
                  )}
                  {selectedInfoTab === '필독사항' && (
                    <div className="tab-content-section">
                      <h3 className="tab-content-title">필독사항</h3>

                      <div className="notice-item-with-icon">
                        <div className="notice-icon" style={{ backgroundColor: 'transparent' }}>
                          <img
                            src={
                              selectedCarrier === 'KT' ? ktLogo :
                              selectedCarrier === 'LG U+' ? uplusLogo :
                              skLogo
                            }
                            alt={selectedCarrier}
                            style={{ width: '24px', height: '24px' }}
                          />
                        </div>
                        <div className="notice-content">
                          <h4 className="notice-subtitle">{selectedCarrier} / 번호이동</h4>
                          <p className="notice-text">통신사만 {selectedCarrier}로 변경돼요. 번호는 바꾸지 않으니 안심하세요!</p>
                        </div>
                      </div>

                      <div className="notice-item-with-icon">
                        <div className="notice-icon" style={{ backgroundColor: 'transparent' }}>
                          <img src={iconToDo} alt="할일" style={{ width: '28px', height: '28px' }} />
                        </div>
                        <div className="notice-content">
                          <h4 className="notice-subtitle">통신사 24개월간 유지</h4>
                          <div className="penalty-grid">
                            <div className="penalty-header">
                              <div className="penalty-header-cell">변경 시점</div>
                              <div className="penalty-header-cell">안내사항</div>
                            </div>
                            <div className="penalty-row">
                              <div className="penalty-cell">개통 다음날부터 만 24개월 약정 기간 내</div>
                              <div className="penalty-cell">사용기간에 따라 공통지원금 또는 약정할인에 대한 위약금 발생</div>
                            </div>
                            <div className="penalty-row">
                              <div className="penalty-cell">개통 다음날부터 만 8개월 이내 해지 또는 통신사 변경</div>
                              <div className="penalty-cell">공통지원금 또는 약정할인에 대한 위약금 발생 + 삐삐 할인금 일부 반환</div>
                            </div>
                            <div className="penalty-row">
                              <div className="penalty-cell">개통 다음날부터 만 6개월 이내 해지 또는 통신사 변경</div>
                              <div className="penalty-cell">공통지원금 또는 약정할인에 대한 위약금 발생 + 삐삐 할인금 전액 반환</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="notice-item-with-icon">
                        <div className="notice-icon" style={{ backgroundColor: 'transparent' }}>
                          <img src={iconMemory} alt="메모리" style={{ width: '28px', height: '28px' }} />
                        </div>
                        <div className="notice-content">
                          <h4 className="notice-subtitle">첫달 유심비 7,700원</h4>
                          <p className="notice-text">첫 달에만 유심비 7,700원을 추가해서 128,300원을 납부해요.</p>
                        </div>
                      </div>

                      <div className="notice-item-with-icon">
                        <div className="notice-icon" style={{ backgroundColor: 'transparent' }}>
                          <img src={iconNotice} alt="알림" style={{ width: '28px', height: '28px' }} />
                        </div>
                        <div className="notice-content">
                          <h4 className="notice-subtitle">알뜰폰에서 번호이동 시 할인금액 조정</h4>
                          <p className="notice-text">알뜰폰에서 번호이동 시 할인금액이 소폭 조정 될 수 있습니다.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedInfoTab === '개통방법' && (
                    <div className="tab-content-section">
                      <h3 className="tab-content-title">개통방법</h3>

                      <div className="activation-flow">
                        <div className="activation-item-with-icon">
                          <div className="activation-icon">
                            <img src={iconSupport} alt="개통 상담" style={{ width: '32px', height: '32px' }} />
                          </div>
                          <div className="activation-content">
                            <h4 className="activation-subtitle">개통 상담</h4>
                            <p className="activation-text">전문 상담사를 통해 개통 여부, 최종적인 금액 등 상담을 진행합니다.</p>
                          </div>
                        </div>

                        <div className="activation-item-with-icon">
                          <div className="activation-icon">
                            <img src={iconCall} alt="삐삐콜" style={{ width: '32px', height: '32px' }} />
                          </div>
                          <div className="activation-content">
                            <h4 className="activation-subtitle">삐삐콜</h4>
                            <p className="activation-text">삐삐에서 고객정보 확인을 위해 삐삐콜을 드려요. (평일 19시까지 신청한 건에 한해 발송, 주말 미운영)</p>
                          </div>
                        </div>

                        <div className="activation-item-with-icon">
                          <div className="activation-icon">
                            <img src={iconBox} alt="택배수령" style={{ width: '32px', height: '32px' }} />
                          </div>
                          <div className="activation-content">
                            <h4 className="activation-subtitle">택배수령</h4>
                            <p className="activation-text">오후 4시 전까지 단말기 배정이 완료된 주문은 당일 발송해드려요. *재고와 배정 상황에 따라 발송 일정이 변경될 수 있어요.</p>
                          </div>
                        </div>

                        <div className="activation-item-with-icon">
                          <div className="activation-icon">
                            <img src={iconCheck} alt="개통완료" style={{ width: '32px', height: '32px' }} />
                          </div>
                          <div className="activation-content">
                            <h4 className="activation-subtitle">개통완료</h4>
                            <p className="activation-text">통신사이동 시, 개통은 1~2주 정도 소요되기 때문에 유심을 가지고 다니시는걸 추천드려요. 기기변경 시, 휴대폰 택배수령 후 유심만 변경해서 넣으시면 돼요.</p>
                          </div>
                        </div>

                        <div className="activation-item-with-icon">
                          <div className="activation-icon">
                            <img src={iconCaution} alt="취소/교환/불량 안내" style={{ width: '32px', height: '32px' }} />
                          </div>
                          <div className="activation-content">
                            <h4 className="activation-subtitle">취소/교환/불량 안내</h4>
                            <p className="activation-text">박스 개봉 및 개통 후에는 모델 변경, 취소, 교환이 불가능해요. 14일 이내로 불량이 발생한 경우 애플 서비스센터에서 교환이 가능해요. 개통 시 인박싱 영상 촬영을 권장 드려요.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* 워치 선택 옵션 */}
        {selectedType === '워치' && selectedWatch && (
          <>
            <div className="divider" />

            <div className="section">
              <div className="section-header-with-toggle">
                <h3 className="section-title">선택 옵션</h3>
                <button
                  className="toggle-btn"
                  onClick={() => setIsOptionsExpanded(!isOptionsExpanded)}
                >
                  {isOptionsExpanded ? '▲' : '▼'}
                </button>
              </div>
            </div>

            {isOptionsExpanded && (
              <>
                <div className="info-card">
                  <img
                    src={getWatchImageUrl()}
                    alt="워치"
                    className="info-image"
                  />
                  <div className="info-text">
                    <p className="info-title">워치</p>
                    <p className="info-desc">
                      {selectedWatch.model_name}
                      {selectedWatchSize ? ` · ${selectedWatchSize}` : ''}
                      {selectedWatchColor ? ` · ${selectedWatchColor.name}` : ''}
                    </p>
                  </div>
                </div>

                <div className="divider" />

                {/* 사이즈 선택 */}
                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">사이즈</h3>
                    <p className="section-desc">원하시는 사이즈를 선택해 주세요.</p>
                  </div>
                  <div className="chip-group">
                    {selectedWatch.available_sizes?.map((size) => (
                      <button
                        key={size}
                        className={`chip ${selectedWatchSize === size ? 'active' : ''}`}
                        onClick={() => setSelectedWatchSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="divider" />

                {/* 색상 선택 */}
                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">색상</h3>
                    <p className="section-desc">원하시는 색상을 선택해 주세요.</p>
                  </div>
                  <div className="color-group">
                    {selectedWatch.available_colors?.map((color) => (
                      <button
                        key={color.name}
                        className={`color-chip ${selectedWatchColor?.name === color.name ? 'active' : ''}`}
                        onClick={() => setSelectedWatchColor(color)}
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      >
                        {selectedWatchColor?.name === color.name && (
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M13.3333 4L6 11.3333L2.66667 8"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="divider" />

                {/* 용량 선택 */}
                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">용량</h3>
                    <p className="section-desc">원하시는 저장 용량을 선택해 주세요.</p>
                  </div>
                  <div className="chip-group">
                    {selectedWatch.available_storages?.map((storage) => (
                      <button
                        key={storage}
                        className={`chip ${selectedWatchStorage === storage ? 'active' : ''}`}
                        onClick={() => setSelectedWatchStorage(storage)}
                      >
                        {storage}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="divider" />

                {/* 통신사 선택 */}
                <div className="section">
                  <div className="section-header">
                    <h3 className="section-title">통신사</h3>
                    <p className="section-desc">이용할 통신사를 선택해 주세요.</p>
                  </div>
                  <div className="chip-group">
                    {['KT', 'SK', 'LG U+', '알뜰폰'].map((carrier) => (
                      <button
                        key={carrier}
                        className={`chip ${selectedCarrier === carrier ? 'active' : ''}`}
                        onClick={() => setSelectedCarrier(carrier)}
                      >
                        {carrier}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {isConsultExpanded && (
        <div
          className="overlay"
          onClick={() => setIsConsultExpanded(false)}
        />
      )}

      <div
        className={`bottom-sheet ${isBottomSheetExpanded ? 'expanded' : ''}`}
        style={{ transform: `translateY(-${dragOffset}px)` }}
      >
        <div
          className="bottom-sheet-handle"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="handle-bar"></div>
        </div>
        <button
          className="detail-expand-btn"
          onClick={() => setIsConsultExpanded(!isConsultExpanded)}
        >
          {isConsultExpanded ? '상세내역 닫기 ▼' : '상세내역 확인 ▲'}
        </button>
        <div className="bottom-sheet-content">
          <div className="price-row">
            <div className="price-label">
              <span className="label-text">구매 원금</span>
            </div>
            <span className="price-value">
              {selectedType === '워치' ? watchDevicePrice.toLocaleString() : phoneDevicePrice.toLocaleString()} 원
            </span>
          </div>

          <div className="benefit-section">
            {isConsultExpanded && (
              <div className="price-detail-expanded">
                {/* 가로 구분선 */}
                <div className="horizontal-divider-list" />

                {/* 워치 섹션 */}
                {selectedType === '워치' && selectedWatch && (
                  <div className="detail-section">
                    <div className="detail-row main">
                      <span className="detail-label">워치</span>
                      <span className="detail-value">{watchDevicePrice.toLocaleString()} 원</span>
                    </div>
                    <div className="detail-row sub">
                      <span className="detail-label">출고가 ({selectedWatchSize} / {selectedWatchStorage})</span>
                      <span className="detail-value">
                        {watchPricing?.original_price?.toLocaleString() || '0'} 원
                      </span>
                    </div>
                    <div className="detail-row sub">
                      <span className="detail-label">공시지원</span>
                      <span className="detail-value">-{watchPricing?.common_support?.toLocaleString() || '0'} 원</span>
                    </div>
                    <div className="detail-row sub highlight">
                      <span className="detail-label">삐삐 할인</span>
                      <span className="detail-value">-{watchPricing?.store_support?.toLocaleString() || '0'} 원</span>
                    </div>
                  </div>
                )}

                {/* 휴대폰 섹션 */}
                {selectedType !== '워치' && (
                  <div className="detail-section">
                    <div className="detail-row main">
                      <span className="detail-label">휴대폰</span>
                      <span className="detail-value">{phoneDevicePrice.toLocaleString()} 원</span>
                    </div>
                    {selectedPhone && (
                      <>
                        <div className="detail-row sub">
                          <span className="detail-label">출고가 ({selectedCapacity})</span>
                          <span className="detail-value">
                            {selectedPhone.storage_prices && selectedPhone.storage_prices[selectedCapacity]
                              ? selectedPhone.storage_prices[selectedCapacity].toLocaleString()
                              : (selectedPhone.original_price?.toLocaleString() || '0')} 원
                          </span>
                        </div>
                        {selectedDiscountMethod === '공시지원' && (
                          <div className="detail-row sub">
                            <span className="detail-label">공시지원</span>
                            <span className="detail-value">-{selectedPhone.common_support?.toLocaleString() || '0'} 원</span>
                          </div>
                        )}
                        <div className="detail-row sub highlight">
                          <span className="detail-label">삐삐 할인</span>
                          <span className="detail-value">-{getBbibbiDiscount().toLocaleString()} 원</span>
                        </div>
                        <div className="detail-row sub">
                          <span className="detail-label">할부</span>
                          <span className="detail-value">{selectedInstallment}</span>
                        </div>
                        {selectedInstallment !== '일시불' && (
                          <>
                            <div className="detail-row sub">
                              <span className="detail-label">단말기 월 할부금</span>
                              <span className="detail-value">{getMonthlyPrincipal().toLocaleString()} 원</span>
                            </div>
                            <div className="detail-row sub">
                              <span className="detail-label">단말기 월 할부이자</span>
                              <span className="detail-value">{getMonthlyInterest().toLocaleString()} 원</span>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* 가로 구분선 - 휴대폰일 때만 요금제 섹션 표시 */}
                {selectedType !== '워치' && (
                  <>
                    <div className="horizontal-divider-list" />

                    {/* 요금제 섹션 */}
                    <div className="detail-section">
                      <div className="detail-row main">
                        <span className="detail-label">요금제</span>
                        <span className="detail-value">{planPrice.toLocaleString()} 원</span>
                      </div>
                      <div className="detail-row sub">
                        <span className="detail-label">{currentPlan.name} {selectedDiscountMethod === '선택약정' && '(189일 유지)'}</span>
                        <span className="detail-value">{basePlanPrice.toLocaleString()} 원</span>
                      </div>
                      {selectedDiscountMethod === '선택약정' && (
                        <div className="detail-row sub">
                          <span className="detail-label">선택약정 25% 할인</span>
                          <span className="detail-value">-{planDiscount.toLocaleString()} 원</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* 부가서비스 섹션 - 휴대폰이고 currentAddons가 있을 때만 표시 */}
                {selectedType !== '워치' && currentAddons && currentAddons.length > 0 && (
                  <>
                    {/* 가로 구분선 */}
                    <div className="horizontal-divider-list" />

                    <div className="detail-section">
                      <div className="detail-row main">
                        <span className="detail-label">부가서비스</span>
                        <span className="detail-value"></span>
                      </div>
                      {currentAddons.map((addon: PhoneAddon, index: number) => (
                        <div key={index} className="detail-row sub" style={{ display: 'flex', alignItems: 'center' }}>
                          <span className="detail-label" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            {addon.name}
                            {addon.required && <span style={{ color: '#ff6b6b', fontSize: '12px', marginLeft: '4px' }}>(필수)</span>}
                          </span>
                          <span className="detail-value" style={{ marginRight: '12px' }}>{addon.price.toLocaleString()} 원</span>
                          <label className={`addon-toggle ${addon.required ? 'disabled' : ''}`} style={{ marginLeft: 0 }}>
                            <input
                              type="checkbox"
                              checked={selectedAddons[addon.name] || false}
                              onChange={(e) => {
                                if (!addon.required) {
                                  setSelectedAddons({
                                    ...selectedAddons,
                                    [addon.name]: e.target.checked
                                  });
                                }
                              }}
                              disabled={addon.required}
                            />
                            <span className="addon-toggle-slider"></span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* 가로 구분선 */}
                <div className="horizontal-divider-list" />

                {/* 월 납부 예상 금액 - 휴대폰 */}
                {selectedType !== '워치' && (
                  <div className="detail-row total">
                    <div className="total-label-group">
                      <span className="total-label">월 납부 예상 금액</span>
                      <span className="total-sub">
                        {selectedInstallment !== '일시불' ? '기기할부 + ' : ''}
                        요금제
                        {currentAddons && currentAddons.length > 0 ? ' + 부가서비스' : ''}
                      </span>
                    </div>
                    <span className="total-value">{totalMonthlyPrice.toLocaleString()} 원</span>
                  </div>
                )}

                {/* 구매 원금 - 워치 */}
                {selectedType === '워치' && (
                  <div className="detail-row total">
                    <div className="total-label-group">
                      <span className="total-label">구매 원금</span>
                      <span className="total-sub">출고가 - 공시지원 - 삐삐 할인</span>
                    </div>
                    <span className="total-value">{watchDevicePrice.toLocaleString()} 원</span>
                  </div>
                )}

                {/* 상담 요청 버튼 */}
                <button className="consult-request-btn" onClick={handleChatConsult}>
                  상담 요청
                </button>
              </div>
            )}
          </div>

          {isBottomSheetExpanded && (
            <div className="expanded-content">
              <div className="divider-thin" />
              <div className="price-section">
                <h3 className="section-title-expanded">월 청구 예상 금액</h3>
                <div className="price-row">
                  <div className="price-label">
                    <span className="label-text">핸드폰 요금</span>
                    <span className="label-sub">(매월)</span>
                  </div>
                  <span className="price-value">{totalMonthlyPrice.toLocaleString()} 원</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhoneList;
